import { Autocomplete, AutocompleteProps, CircularProgress, TextField, TextFieldProps } from '@mui/material';
import React from 'react';
import { Observable } from 'rxjs';

import { useDebounceObservable } from 'app/hooks';
import { KnpPaginationData } from 'app/model';
import { EntityOnChange } from 'app/type';

type ExcludeFields =
    | 'autoComplete'
    | 'filterOptions'
    | 'multiple'
    | 'onChange'
    | 'onInputChange'
    | 'options'
    | 'renderInput'
    | 'value';

export interface EntityCollectionAutocompleteProps<T extends object, V = unknown, S = KnpPaginationData<V>>
    extends Omit<AutocompleteProps<V, true, false, false>, ExcludeFields>,
        Pick<TextFieldProps, 'label'> {
    name: string & keyof T;
    value: T;
    onChange: EntityOnChange<T, readonly V[]>;
    onSearch: (data: string) => Observable<S>;
}

export const EntityCollectionAutocomplete = <E extends object, V = unknown>(
    props: EntityCollectionAutocompleteProps<E, V>,
): React.ReactElement => {
    const { value, onChange, onSearch, label, ...rest } = props;

    const { loading, result: options, setInput } = useDebounceObservable(onSearch, (page) => page.data);

    const innerOnChange: AutocompleteProps<V, true, false, false>['onChange'] = (evt, newValue: readonly V[]) => {
        onChange({ target: { name: props.name, value: newValue } }, props.name, newValue);
    };

    const data = value[props.name] as unknown as V[];

    return (
        <Autocomplete
            options={options || []}
            onChange={innerOnChange}
            value={data}
            onInputChange={(evt, newInputValue) => {
                setInput(newInputValue);
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant='standard'
                    label={label}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color='inherit' size={20} /> : undefined}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            /* Because we search as we type disable build in filtering */
            filterOptions={(x) => x}
            multiple
            autoComplete
            /* the rest are overridable */
            filterSelectedOptions
            {...rest}
        />
    );
};
